import { fetchUserChatProfile, fetchUserChatProfiles } from "@/lib/api/users";
import { getApiEnvironmentKey } from "@/lib/api/client";
import { getAuthUser } from "@/lib/api/token-store";
import { getThreadFlagStrings } from "@/lib/thread-flags";
import { appDayDiff, appDayKey, appIsSameDay, withAppTimeZone } from "@/lib/timezone";
import {
  DEFAULT_THREAD_METADATA_DEFAULT_LABEL,
  DEFAULT_THREAD_UNNAMED_LABEL,
  MESSAGES_RIGHT_PANEL_OPEN_KEY,
  MESSAGES_SELECTED_THREAD_STORAGE_PREFIX,
} from "@/lib/messages/messages-constants";
import {
  getMessageDedupKey,
  isCallLogMessage,
  isMetadataAutoReplyMessage,
  isStandaloneSystemLineMessage,
  parseMessageBodyText,
} from "@/lib/realtime/chat-message-helpers";
import { businessReaction } from "@/lib/messages/reactions-helpers";
import { formatWaGroupSystemLine } from "@/lib/messages/wa-groups";

export function readMessagesRightPanelOpen() {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    return window.localStorage.getItem(MESSAGES_RIGHT_PANEL_OPEN_KEY) !== "0";
  } catch {
    return true;
  }
}

export function getMessagesSelectedThreadStorageKey(scope) {
  const uid = String(getAuthUser()?.id || "").trim();
  if (!uid) return null;
  // Each pane keeps its own selection. The default scope keeps the legacy unsuffixed SCOPE part;
  // every other scope gets its own suffix.
  //
  // This used to be `internal ? ":internal" : ""`, which meant a NEW scope silently inherited the
  // Messages pane's saved id — so opening Private restored a patient thread and the server
  // answered "user is not a member of this thread".
  const normalized = String(scope || "").trim().toLowerCase();
  const suffix = normalized && normalized !== "normal" ? `:${normalized}` : "";
  // The key is ALSO scoped by API environment: one app profile serves dev and staging alike
  // (dev repoints VITE_API_BASE_URL), and user ids match across them (dev Mongo is a staging
  // clone) — so without this, a thread selected under dev was probed against staging on the
  // next launch and 403'd ("user is not a member of this thread") on every start. Environment
  // ids only mean something within their own backend. Deliberately NOT backward compatible:
  // pre-env keys are simply never read again (a one-time "no thread preselected" on upgrade),
  // which also inertly strands any cross-environment id those keys still hold.
  return `${MESSAGES_SELECTED_THREAD_STORAGE_PREFIX}:${uid}${suffix}@${getApiEnvironmentKey()}`;
}

export function readPersistedSelectedThreadId(scope) {
  const key = getMessagesSelectedThreadStorageKey(scope);
  if (!key || typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const id = String(window.localStorage.getItem(key) || "").trim();
    return id || null;
  } catch (_error) {
    return null;
  }
}

export function persistSelectedThreadId(threadId, scope) {
  const key = getMessagesSelectedThreadStorageKey(scope);
  if (!key || typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    const id = String(threadId || "").trim();
    if (id) {
      window.localStorage.setItem(key, id);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch (_error) {
    // ignore quota / private mode
  }
}

/**
 * Whether an (internal) thread row is a 1:1 direct chat rather than a named group.
 * `metadata.internalKind` is authoritative: the server stamps type "direct" onto ANY
 * thread with ≤2 members, so a two-person NAMED group still carries type "direct" —
 * `thread.type` only decides for legacy rows that predate the kind.
 */
export function isInternalDirectThread(thread) {
  const kind = String(thread?.metadata?.internalKind ?? "").toLowerCase();
  if (kind) return kind === "direct";
  return String(thread?.type ?? "").toLowerCase() === "direct";
}

export function getThreadDisplayName(thread, { isInternal = false } = {}) {
  if (thread?.metadata?.default) {
    return { label: DEFAULT_THREAD_METADATA_DEFAULT_LABEL, isPlaceholder: true };
  }
  const title = String(thread?.title ?? "").trim();
  const name = String(thread?.name ?? "").trim();
  // Internal group chats carry a human-given name, but the server's `title` is the participant
  // list ("other members" joined). For a group the explicit name should win; a 1:1 keeps the
  // title (the other person), since a direct chat's stored name is the generic "Direct Chat".
  // Patient/Messages threads (isInternal false) always keep title/MemberTitle precedence.
  const isDirect = isInternalDirectThread(thread);
  if (isInternal && !isDirect && name) {
    return { label: name, isPlaceholder: false };
  }
  if (title) {
    return { label: title, isPlaceholder: false };
  }
  if (!name) {
    return { label: DEFAULT_THREAD_UNNAMED_LABEL, isPlaceholder: true };
  }
  return { label: name, isPlaceholder: false };
}

/**
 * The identity fact NOT already shown as the thread's title, rendered by callers as a smaller
 * muted "(…)" suffix so unresolved WhatsApp contacts keep both facts visible: the patient's
 * self-chosen WhatsApp name next to a phone title, or the phone next to a push-name title.
 * Server enrichment provides the pieces (`titleSource` / `whatsappPushName` / `whatsappPhone`);
 * resolved patients (titleSource "member") stay clean — their phone lives in the side panel.
 */
export function getThreadSecondaryIdentity(thread, { isInternal = false } = {}) {
  if (!thread || isInternal) return "";
  const source = String(thread.titleSource || "").trim();
  const pushName = String(thread.whatsappPushName || "").trim();
  const phone = String(thread.whatsappPhone || "").trim();
  if (source === "phone") return pushName;
  if (source === "pushName") return phone;
  return "";
}

export function getThreadRowId(thread) {
  return String(thread?.threadId || thread?.id || "").trim();
}

/** Sidebar row synthesized when the thread list has not caught up with a WS message yet. */
export function buildOptimisticThreadFromMessage(msg) {
  const threadId = String(msg?.threadId || "").trim();
  if (!threadId) return null;
  const preview = parseMessageBodyText(msg?.body);
  const sender = msg?.sender;
  const lastMessageSender =
    sender && String(sender.userId || "").trim()
      ? {
          userId: String(sender.userId).trim(),
          kind: String(sender.kind || "user").toLowerCase() === "bot" ? "bot" : "user",
        }
      : undefined;
  return {
    threadId,
    lastMessageAt: msg.createdAt || new Date().toISOString(),
    lastMessagePreview: preview || "New message",
    ...(lastMessageSender ? { lastMessageSender } : {}),
    __optimistic: true,
  };
}

/** WhatsApp bridge: staff reply sent from the business account (`fromMe` on the WA side). */
export function isWhatsAppStaffAnswerMessage(message) {
  return Boolean(message?.metadata?.whatsapp?.fromMe);
}

/**
 * WhatsApp bridge: message revoked on WhatsApp ("delete for everyone"). The content is kept —
 * render it struck-through with a "Deleted" cue instead of dropping the bubble.
 */
export function isWhatsAppDeletedMessage(message) {
  return message?.metadata?.whatsapp?.deleted === true;
}

/**
 * A "delete for everyone" WE requested is still waiting on WhatsApp's verdict. The bubble is
 * already struck through at this point — the retraction was asked for — but we cannot yet say
 * whether the patient's copy is gone.
 */
export function isWhatsAppDeletePending(message) {
  return message?.metadata?.whatsapp?.deletePending === true;
}

/**
 * WhatsApp refused a "delete for everyone" we requested (past its retraction window, session down,
 * not revocable). The message stays struck through on our side — the attempt is part of the record
 * — but the patient still has it, which is what the cue must say.
 */
export function isWhatsAppDeleteFailed(message) {
  return message?.metadata?.whatsapp?.deleteFailed === true;
}

/** Why WhatsApp refused the delete, as reported by the gateway. "" when we have no detail. */
export function getWhatsAppDeleteError(message) {
  const reason = message?.metadata?.whatsapp?.deleteError;
  return typeof reason === "string" ? reason.trim() : "";
}

/** WhatsApp bridge: the WA message id of THIS message (set on bridged inbound messages). */
export function getWhatsAppMessageId(message) {
  const id = message?.metadata?.whatsapp?.messageId;
  return typeof id === "string" ? id.trim() : "";
}

/**
 * WhatsApp bridge: the WA message id this message QUOTES (native WhatsApp reply-to).
 * Set by healo_bot on replies that raced with newer patient messages — the bot answers
 * the older message as a quote and skips the raced one, so the quote is the only cue
 * tying the reply to the message it answers. Render it like WhatsApp does.
 */
export function getWhatsAppQuotedMessageId(message) {
  const id = message?.metadata?.whatsapp?.quotedMessageId;
  return typeof id === "string" ? id.trim() : "";
}

/**
 * The patient's own WhatsApp identity for an INBOUND patient WhatsApp message, taken from the
 * message's `metadata.whatsapp`: their WhatsApp name with the phone in parentheses
 * (`Jane Doe (6596411255)`) so staff keep the number, or the phone alone when there's no name.
 *
 * Used as the sender label ONLY when the WhatsApp number has no real patient record to show — i.e.
 * an unmatched/unsynced number, which the backend relays under a nameless bridge external-stub
 * account (see {@link isWhatsAppBridgeStubName}). A synced/named patient keeps their real name, so
 * callers gate on that and only fall back here when there is no real name.
 *
 * Returns "" for anything that is NOT an inbound patient WhatsApp message — staff answers sent from
 * the business number (`fromMe`), the bot's own replies (`kind === "bot"`), and non-WhatsApp
 * messages.
 *
 * @param {object} message
 * @returns {string}
 */
export function getWhatsAppPatientSenderLabel(message) {
  const wa = message?.metadata?.whatsapp;
  if (!wa || wa.fromMe) return "";
  if (String(message?.sender?.kind || "").toLowerCase() === "bot") return "";
  const phone = String(wa.phoneNumber || "").trim();
  const pushName = getWhatsAppPatientPushName(message);
  if (pushName) {
    return phone ? `${pushName} (${phone})` : pushName;
  }
  return phone;
}

/**
 * The patient's own WhatsApp display name (`pushName`) for an INBOUND patient WhatsApp message —
 * the human name with no phone attached (e.g. `"Jane Doe"`), or `""` when there is none.
 *
 * Same gating as {@link getWhatsAppPatientSenderLabel} (skips staff `fromMe` answers and the bot's
 * own replies) and the same `"Unknown"` sentinel handling — that bridge placeholder is not a name.
 * Use this (rather than the sender-label helper) when a phone number is already shown separately and
 * you only want the name part, e.g. labelling an unmatched WhatsApp patient in the participants
 * panel whose own row already carries the phone line.
 *
 * @param {object} message
 * @returns {string}
 */
export function getWhatsAppPatientPushName(message) {
  const wa = message?.metadata?.whatsapp;
  if (!wa || wa.fromMe) return "";
  if (String(message?.sender?.kind || "").toLowerCase() === "bot") return "";
  const pushName = String(wa.pushName || "").trim();
  // `"Unknown"` is the bridge's sentinel for a patient with no WhatsApp name — not patient data.
  return pushName && pushName.toLowerCase() !== "unknown" ? pushName : "";
}

/**
 * True when a resolved chat-profile display name is really a WhatsApp bridge external-stub LOGIN ID
 * (`ext-bot-<botId>-<phone>`; see backend `botExternalLoginID`) rather than a person's name.
 *
 * When an inbound WhatsApp number has not been matched/synced to a real patient, the backend creates
 * a nameless external-stub user for it; with no profile name, the chat-profile endpoint returns that
 * user's login id as the "display name". Detecting that lets callers show the patient's WhatsApp
 * identity (name/phone) instead of a relay login id, while leaving synced/named patients untouched.
 *
 * @param {string | null | undefined} resolvedDisplayName
 * @returns {boolean}
 */
export function isWhatsAppBridgeStubName(resolvedDisplayName) {
  return String(resolvedDisplayName || "").trim().toLowerCase().startsWith("ext-bot-");
}

/**
 * Map message id → message, across the loaded clusters, for resolving
 * `quotedMessageId` to the original quoted message within the thread history.
 * Indexes BOTH the WhatsApp bridge id (`metadata.whatsapp.messageId`) and the
 * backend message `id` — the bot quotes by WA id on WhatsApp threads and may
 * reference backend ids elsewhere.
 * @param {Array} messageClusters — output of {@link clusterHistoryMessages}
 * @returns {Map<string, object>}
 */
export function buildWhatsAppMessageIdIndex(messageClusters) {
  const index = new Map();
  for (const cluster of messageClusters || []) {
    const items = cluster?.items || (cluster?.item ? [cluster.item] : []);
    for (const msg of items) {
      const waId = getWhatsAppMessageId(msg);
      if (waId && !index.has(waId)) {
        index.set(waId, msg);
      }
      const plainId = msg?.id ? String(msg.id) : "";
      if (plainId && !index.has(plainId)) {
        index.set(plainId, msg);
      }
    }
  }
  return index;
}

export function getSenderGroupKey(message) {
  if (!message || isStandaloneSystemLineMessage(message)) return null;
  const senderId = String(message?.sender?.userId || "").trim();
  const kind = String(message?.sender?.kind || "user").toLowerCase();
  if (!senderId) return null;
  const waStaff = isWhatsAppStaffAnswerMessage(message) ? ":wa_staff" : "";
  return `${senderId}:${kind}${waStaff}`;
}

export function normalizeMemberSource(raw) {
  const v = String(raw || "direct").toLowerCase();
  if (v === "team" || v === "both") return v;
  return "direct";
}

/**
 * Same direct/team split as GET …/members handling in use-messages-page.
 * @param {Array<{ userId?: string, source?: string }>} users
 * @param {string[] | undefined} legacyUserIds
 * @param {string[] | undefined} botIds
 * @returns {{ directUserIds: string[], teamUserIds: string[], botIds: string[] }}
 */
export function parseThreadMemberIdsFromMembersPayload(users, legacyUserIds, botIds) {
  const usersInput =
    Array.isArray(users) && users.length > 0
      ? users
      : Array.isArray(legacyUserIds)
        ? legacyUserIds.map((id) => ({ userId: id, source: "direct" }))
        : [];
  const seenUserIds = new Set();
  const uniqueMembers = [];
  for (const u of usersInput) {
    const userId = String(u?.userId || "").trim();
    if (!userId || seenUserIds.has(userId)) continue;
    seenUserIds.add(userId);
    uniqueMembers.push({ userId, source: normalizeMemberSource(u?.source) });
  }
  const primaryMembership = uniqueMembers.filter((m) => m.source === "direct" || m.source === "both");
  const teamMembership = uniqueMembers.filter((m) => m.source === "team");
  return {
    directUserIds: primaryMembership.map((m) => m.userId),
    teamUserIds: teamMembership.map((m) => m.userId),
    botIds: Array.isArray(botIds) ? botIds.map((id) => String(id || "").trim()).filter(Boolean) : [],
  };
}

/**
 * DEV tooling: pick a direct thread member user id to attribute an outbound message as the patient.
 * Prefers a non-self row whose admin snapshot `userType` is `patient`; otherwise first non-self direct row.
 *
 * @param {Array<{ id?: string, isSelf?: boolean, adminUserSnapshot?: { userType?: string } | null }>} directRows
 * @param {string} [currentUserId]
 * @returns {string} trimmed user id or empty string if none
 */
export function resolveDevPatientImpersonationUserId(directRows, currentUserId) {
  const rows = Array.isArray(directRows) ? directRows : [];
  const nonSelf = rows.filter((r) => {
    if (r?.isSelf) return false;
    const id = String(r?.id || "").trim();
    return Boolean(id);
  });
  if (!nonSelf.length) {
    return "";
  }
  const patientRow = nonSelf.find((r) => {
    const ut = String(r?.adminUserSnapshot?.userType || "").trim().toLowerCase();
    return ut === "patient";
  });
  const pick = patientRow || nonSelf[0];
  return String(pick?.id || "").trim();
}

/**
 * @returns {{ userId: string, kind: 'user' | 'bot' } | null}
 */
export function normalizeLastMessageSender(thread) {
  const raw = thread?.lastMessageSender;
  if (!raw || typeof raw !== "object") return null;
  const userId = String(raw.userId ?? "").trim();
  if (!userId) return null;
  const kind = String(raw.kind ?? "user").toLowerCase() === "bot" ? "bot" : "user";
  return { userId, kind };
}

/**
 * Normalize one API thread row, resolving lastMessageSender consistently across initial load,
 * range fetches and search: trust the API sender, else clear it for empty/preview-less threads,
 * else keep the previously known sender so a row never flickers to "unknown" on a refresh.
 */
export function normalizeApiThreadRow(thread, old) {
  const fromApi = normalizeLastMessageSender(thread);
  const rawLastAt = thread?.lastMessageAt;
  const hasLastMessageAt =
    rawLastAt != null && rawLastAt !== "" && !Number.isNaN(new Date(rawLastAt).getTime());
  const previewTrim = String(thread?.lastMessagePreview || "").trim();
  const previewMeansEmpty = !previewTrim || /^no messages yet$/i.test(previewTrim);
  let lastMessageSender;
  if (fromApi != null) {
    lastMessageSender = fromApi;
  } else if (!hasLastMessageAt) {
    lastMessageSender = null;
  } else if (previewMeansEmpty) {
    lastMessageSender = null;
  } else {
    lastMessageSender = old?.lastMessageSender ?? null;
  }
  return { ...thread, lastMessageSender };
}

/**
 * English relative time since last message; minimum resolution 1 minute.
 * @param {string | number | Date} isoDate
 * @param {number} [nowMs]
 */
export function formatEnglishElapsedSinceMessage(isoDate, nowMs = Date.now()) {
  const t = new Date(isoDate || "").getTime();
  if (Number.isNaN(t)) return null;
  const elapsedMs = Math.max(0, nowMs - t);
  const minutesTotal = Math.floor(elapsedMs / 60000);
  const minutes = Math.max(1, minutesTotal);
  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }
  const hoursTotal = Math.floor(elapsedMs / 3600000);
  const hours = Math.max(1, hoursTotal);
  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }
  const daysTotal = Math.floor(elapsedMs / 86400000);
  const days = Math.max(1, daysTotal);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/**
 * WhatsApp: show elapsed time since last message only when that message was from a direct member
 * (patient), not a bot or a team-only participant. Requires membership snapshot and lastMessageSender on thread.
 * @param {object | null | undefined} membership — from parseThreadMemberIdsFromMembersPayload
 * @param {number} [nowMs]
 * @returns {string | null}
 */
export function getWhatsAppMemberReplyWaitLabel(thread, membership, nowMs = Date.now()) {
  if (String(thread?.metadata?.conversationType || "").toLowerCase() !== "whatsapp") return null;
  const rawTime = thread?.lastMessageAt;
  if (!rawTime) return null;
  const sender = normalizeLastMessageSender(thread);
  if (!sender || sender.kind === "bot") return null;
  if (
    !membership ||
    (!membership.directUserIds?.length && !membership.teamUserIds?.length && !membership.botIds?.length)
  ) {
    return null;
  }
  const uid = sender.userId;
  const teamSet = new Set(membership.teamUserIds || []);
  const directSet = new Set(membership.directUserIds || []);
  const botSet = new Set(membership.botIds || []);
  if (botSet.has(uid)) return null;
  if (teamSet.has(uid)) return null;
  if (!directSet.has(uid)) return null;
  return formatEnglishElapsedSinceMessage(rawTime, nowMs);
}

/**
 * Integer minutes a WhatsApp patient has been waiting for a reply.
 * Uses server-provided fields only (`lastMessageAt`, `lastMessageSender.kind`) so the chip
 * renders immediately from thread-list data. When membership info has been loaded for the
 * thread, it's used to hide the chip for team-member replies; without membership we assume
 * a non-bot sender is the customer and show the wait time.
 * @param {object} thread
 * @param {{ directUserIds?: string[], teamUserIds?: string[], botIds?: string[] } | null | undefined} membership
 * @param {number} [nowMs]
 * @returns {number | null}
 */
export function getWhatsAppWaitingMinutes(thread, membership, nowMs = Date.now()) {
  // TODO: keep commented out for now
  // if (String(thread?.metadata?.conversationType || "").toLowerCase() !== "whatsapp") return null;
  const rawTime = thread?.lastMessageAt;
  if (!rawTime) return null;
  const sender = normalizeLastMessageSender(thread);
  // TODO: keep commented out for now
  // if (!sender || sender.kind === "bot") return null;
  // if (membership) {
  //   const uid = sender.userId;
  //   if ((membership.botIds || []).includes(uid)) return null;
  //   if ((membership.teamUserIds || []).includes(uid)) return null;
  // }
  const t = new Date(rawTime).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((nowMs - t) / 60000));
}

// DISABLED (for now): the staff_handles flag is retired — legacy-flagged threads no
// longer surface in "Waiting for you".
//
// /**
//  * WhatsApp + `staff_handles`: time since last message when that message was from a bot or a human
//  * (any non-missing `lastMessageSender`), so the thread can appear in "Waiting for you" even after
//  * a bot or staff reply when human follow-up is still required.
//  * Acknowledging the last message ("Done for now") keeps the flag but drops the thread out of
//  * "Waiting for you"; a later message resets `lastMessageAcknowledgedAt` (the `lastMessageId`
//  * changes) so the thread returns here automatically.
//  * @param {object} thread
//  * @param {number} [nowMs]
//  * @returns {number | null}
//  */
// function getStaffHandlesInboxWaitingMinutes(thread, nowMs = Date.now()) {
//   // TODO: keep commented out for now
//   // if (String(thread?.metadata?.conversationType || "").toLowerCase() !== "whatsapp") return null;
//   const flags = getThreadFlagStrings(thread);
//   if (!flags.includes("staff_handles")) return null;
//   if (thread?.lastMessageAcknowledgedAt) return null; // "Done for now" — keep the flag, drop from "Waiting for you"
//   const sender = normalizeLastMessageSender(thread);
//   if (!sender) return null;
//   const rawTime = thread?.lastMessageAt;
//   if (!rawTime) return null;
//   const t = new Date(rawTime).getTime();
//   if (Number.isNaN(t)) return null;
//   return Math.max(0, Math.floor((nowMs - t) / 60000));
// }

/**
 * Threads qualify only when the server reports a real last message time — avoids “Waiting for you” after
 * clears or empty chats. Same preview rules as {@link getThreadPreview}; attachment-only rows keep a sender.
 * @param {object} thread
 * @returns {boolean}
 */
/**
 * A peer-to-peer conversation: internal staff chats and private contact chats.
 *
 * These have no triage semantics at all. "Unanswered patient reply", "Waiting for you" and
 * "Unanswered bot replies" are all statements about a CLINIC answering a patient on a channel the
 * clinic staffs — none of which describes two people messaging each other.
 *
 * Worth stating why this is a positive check rather than "does the thread have a patient": a
 * private chat between a patient and a staff member DOES have a patientUserId, and the patient
 * genuinely did send the last message. Every triage predicate therefore fired on it and the
 * conversation grew an "Unanswered patient reply" banner with an Acknowledge button — asking a
 * colleague to trim their own private conversation out of a clinical work queue it was never in.
 * Internal chats escaped that only by accident: they have no patient at all.
 */
export function isPeerToPeerThread(thread) {
  const metadata = thread?.metadata;
  if (metadata && (metadata.internal === true || metadata.internalKind)) return true;
  return String(metadata?.conversationType || '').trim().toLowerCase() === 'app_direct';
}

function threadHasConversationForWaitingSegment(thread) {
  // Peer-to-peer first: no triage question is meaningful here, so none of the callers below should
  // even compute an answer.
  if (isPeerToPeerThread(thread)) return false;
  const rawTime = thread?.lastMessageAt;
  if (rawTime == null || rawTime === "") {
    return false;
  }
  if (Number.isNaN(new Date(rawTime).getTime())) {
    return false;
  }
  const preview = String(thread?.lastMessagePreview || "").trim();
  if (preview && !/^no messages yet$/i.test(preview)) {
    return true;
  }
  return normalizeLastMessageSender(thread) != null;
}

/**
 * Minutes used for "Waiting for you" segmentation and row chip: patient reply wait when `locked`.
 * Threads with no messages yet (empty preview and no last sender) never qualify — they belong with the rest of the list only.
 * DISABLED (for now): the staff_handles arm — the flag is retired.
 * @param {object} thread
 * @param {{ directUserIds?: string[], teamUserIds?: string[], botIds?: string[] } | null | undefined} membership
 * @param {number} [nowMs]
 * @returns {number | null}
 */
export function getWhatsAppInboxWaitingMinutes(thread, membership, nowMs = Date.now()) {
  if (!threadHasConversationForWaitingSegment(thread)) {
    return null;
  }
  const flags = getThreadFlagStrings(thread);
  if (flags.includes("locked")) {
    const patientMin = getWhatsAppWaitingMinutes(thread, membership, nowMs);
    if (patientMin != null) return patientMin;
  }
  // if (flags.includes("staff_handles")) {
  //   return getStaffHandlesInboxWaitingMinutes(thread, nowMs);
  // }
  return null;
}

/** Fallbacks used when a caller doesn't pass the user-configured values. */
export const BOT_ACK_DEFAULT_THRESHOLD_MINUTES = 3;
export const BOT_ACK_DEFAULT_MAX_HOURS = 48;

/**
 * The "Unanswered patient reply" window. Same shape as the bot-ack pair and the same threshold
 * (the grace that lets the bot answer first), but a much longer ceiling: these two used to share
 * one setting, which meant a patient nobody replied to silently vanished from "Needs you" after
 * two days. Mirrors the server's DefaultPatientReply* in internal/messagessettings.
 */
export const PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES = BOT_ACK_DEFAULT_THRESHOLD_MINUTES;
export const PATIENT_REPLY_DEFAULT_MAX_HOURS = 168;

/**
 * Minutes for the "Unanswered bot replies" section. A thread qualifies when: it has a real
 * conversation, it is NOT already in "Waiting for you" (no `locked` flag),
 * its last message is from a bot, that message is not acknowledged, and the elapsed time is
 * within `[thresholdMin, maxHours)`. Returns the elapsed minutes, else null.
 * One predicate drives the list section, the row chip, and the header banner so they agree.
 * DISABLED (for now): the retired staff_handles flag no longer excludes a thread here.
 * @param {object} thread
 * @param {number} [thresholdMin]
 * @param {number} [maxHours]
 * @param {number} [nowMs]
 * @returns {number | null}
 */
export function getBotAckWaitingMinutes(
  thread,
  thresholdMin = BOT_ACK_DEFAULT_THRESHOLD_MINUTES,
  maxHours = BOT_ACK_DEFAULT_MAX_HOURS,
  nowMs = Date.now(),
) {
  if (!threadHasConversationForWaitingSegment(thread)) return null;
  const flags = getThreadFlagStrings(thread);
  // Already surfaced under "Waiting for you" — never double-list.
  if (flags.includes("locked") /* || flags.includes("staff_handles") */) return null;
  const sender = normalizeLastMessageSender(thread);
  if (!sender || sender.kind !== "bot") return null;
  if (thread?.lastMessageAcknowledgedAt) return null;
  const t = new Date(thread?.lastMessageAt).getTime();
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.floor((nowMs - t) / 60000));
  if (min < thresholdMin) return null; // too fresh
  if (min >= maxHours * 60) return null; // stale — ignore
  return min;
}

function hasLastMessageBusinessReaction(thread) {
  const reactions =
    thread?.lastMessageReactions ??
    thread?.lastMessage?.reactions ??
    thread?.lastMessage?.message?.reactions ??
    null;
  if (!reactions) return false;
  return Boolean(businessReaction({ reactions }));
}

/**
 * Minutes for the "Unanswered patient reply" section — the mirror of
 * {@link getBotAckWaitingMinutes}. A thread qualifies when: it has a real conversation, it is NOT
 * already in "Waiting for you" (no `locked` flag), its last message is from the PATIENT
 * (`lastMessageSender.userId === thread.patientUserId` AND not `lastMessageFromMe`), that
 * message is not acknowledged or staff-reacted, and the elapsed time is within
 * `[thresholdMin, maxHours)`. A staff reply does NOT count — that's answered: app-channel staff
 * replies are excluded by the userId mismatch, WhatsApp business-account (`fromMe`) staff replies
 * by the flag, and the team's reaction on the last patient message also counts as handled.
 * Returns the elapsed minutes, else null. The threshold grace conveniently lets the bot answer
 * before a patient message is flagged; the ceiling is the patient-reply window's own (NOT
 * bot-ack's — sharing it used to hide anyone unanswered for more than two days).
 * One predicate drives the list section, the row chip, and the header banner.
 * @param {object} thread
 * @param {number} [thresholdMin]
 * @param {number} [maxHours]
 * @param {number} [nowMs]
 * @returns {number | null}
 */
export function getPatientReplyWaitingMinutes(
  thread,
  thresholdMin = PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES,
  maxHours = PATIENT_REPLY_DEFAULT_MAX_HOURS,
  nowMs = Date.now(),
) {
  if (!threadHasConversationForWaitingSegment(thread)) return null;
  const flags = getThreadFlagStrings(thread);
  // Already surfaced under "Waiting for you" — never double-list.
  if (flags.includes("locked")) return null;
  const sender = normalizeLastMessageSender(thread);
  if (!sender || sender.kind !== "user") return null; // bot-last → "Unanswered bot replies"
  // A WhatsApp business-account outbound (`fromMe`) is a STAFF answer — exclude it even though it
  // can be kind "user" carrying the patient's own WhatsApp userId (so the userId match below
  // would otherwise pass).
  if (thread?.lastMessageFromMe) return null;
  const patientUserId = String(thread?.patientUserId || "").trim();
  if (!patientUserId || sender.userId !== patientUserId) return null; // app-channel staff reply ≠ patient
  if (thread?.lastMessageAcknowledgedAt) return null;
  if (hasLastMessageBusinessReaction(thread)) return null;
  const t = new Date(thread?.lastMessageAt).getTime();
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.floor((nowMs - t) / 60000));
  if (min < thresholdMin) return null; // too fresh — the bot may still answer
  if (min >= maxHours * 60) return null; // stale — ignore
  return min;
}

/**
 * Accessible / tooltip label for {@link getWhatsAppInboxWaitingMinutes} (patient-wait copy).
 * DISABLED (for now): the staff_handles generic elapsed-time arm — the flag is retired.
 * @param {object} thread
 * @param {{ directUserIds?: string[], teamUserIds?: string[], botIds?: string[] } | null | undefined} membership
 * @param {number} [nowMs]
 * @returns {string | null}
 */
export function getWhatsAppInboxWaitingLabel(thread, membership, nowMs = Date.now()) {
  const flags = getThreadFlagStrings(thread);
  if (flags.includes("locked")) {
    const patientLabel = getWhatsAppMemberReplyWaitLabel(thread, membership, nowMs);
    if (patientLabel) return patientLabel;
  }
  // if (getStaffHandlesInboxWaitingMinutes(thread, nowMs) == null) return null;
  // return formatEnglishElapsedSinceMessage(thread?.lastMessageAt, nowMs);
  return null;
}

/**
 * Returns escalation tier for a wait duration (minutes).
 *  0 = no waiting state (null), 1 = <3 min incl. "now" (green), 2 = 3–7 min (amber), 3 = >7 min (red)
 * @param {number | null | undefined} min
 */
export function waitTier(min) {
  if (min == null) return 0;
  if (min < 3) return 1;
  if (min <= 7) return 2;
  return 3;
}

/**
 * Tailwind class bundles for each wait tier — mirrors the handoff proposition's
 * waitChip (tinted bg, tier-colored border for >=2, pulse for tier 3) + a matching dot color
 * and section-header styling.
 */
export const TIER_STYLES = {
  0: {
    chip: "",
    dot: "",
    sectionText: "text-muted-foreground",
    sectionBg: "",
  },
  1: {
    chip: "bg-emerald-100 text-emerald-700 border border-transparent dark:bg-emerald-400/15 dark:text-emerald-400",
    dot: "bg-emerald-600 dark:bg-emerald-400",
    sectionText: "text-emerald-700 dark:text-emerald-400",
    sectionBg: "",
  },
  2: {
    chip: "bg-amber-100 text-amber-700 border border-amber-500 dark:bg-amber-400/15 dark:text-amber-400 dark:border-amber-400",
    dot: "bg-amber-600 dark:bg-amber-400",
    sectionText: "text-amber-700 dark:text-amber-400",
    sectionBg: "bg-amber-50/70 dark:bg-amber-950/30",
  },
  3: {
    chip: "bg-red-100 text-red-700 border border-red-500 animate-pulse dark:bg-red-400/15 dark:text-red-400 dark:border-red-400",
    dot: "bg-red-600 dark:bg-red-400",
    sectionText: "text-red-700 dark:text-red-400",
    sectionBg: "bg-red-50/70 dark:bg-red-950/30",
  },
};

/**
 * Short human label for a wait duration (minutes). `0` -> "now", `7` -> "7 min", `62` -> "1h 2m".
 * @param {number | null | undefined} min
 */
export function formatWaitShort(min) {
  if (min == null) return "";
  if (min < 1) return "now";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Subtitle for thread membership `source` from GET …/members (`direct` | `team` | `both`). */
export function membershipSubtitle(source, isSelf) {
  if (source === "team") return "Team access";
  if (source === "both") {
    return isSelf ? "You · active + team" : "Active · also via team";
  }
  return isSelf ? "You · active" : "Active";
}

/** Bot ids have no chat-profile; format for display, e.g. `healo_assistant` → `Healo Assistant`. */
export function formatBotDisplayLabel(botId) {
  const raw = String(botId ?? "").trim();
  if (!raw) return "";
  return raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/** `metadata.botExternalAmbiguousMatchUserIds` from admin user document. */
export function normalizeBotExternalMatchUserIds(metadata) {
  const raw = metadata?.botExternalAmbiguousMatchUserIds;
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((id) => String(id || "").trim()).filter(Boolean))];
}

export function adminUserShowsAmbiguousPanel(adminUser) {
  const meta = adminUser?.metadata;
  if (!meta) return false;
  if (meta.botExternalAmbiguousLookupDuplicate) return true;
  return normalizeBotExternalMatchUserIds(meta).length > 0;
}

export function initialsFromLabel(label) {
  const normalized = String(label || "").trim();
  if (!normalized) return "?";
  return normalized
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

/**
 * Fallback when admin members API is not available (e.g. 403): unique senders from loaded history.
 * @param {(userId: string) => boolean} isCurrentUserId
 */
export function buildThreadMemberRowsFromHistory(historyItems, userDisplayNamesById, isCurrentUserId) {
  const byKey = new Map();
  for (const msg of historyItems) {
    if (isStandaloneSystemLineMessage(msg)) continue;
    const senderId = String(msg?.sender?.userId || "").trim();
    if (!senderId) continue;
    const bot = String(msg?.sender?.kind || "").toLowerCase() === "bot";
    const key = bot ? `bot:${senderId}` : `user:${senderId}`;
    if (byKey.has(key)) continue;
    const label = bot
      ? formatBotDisplayLabel(senderId)
      : (userDisplayNamesById[senderId] || senderId).replace(/_/g, " ");
    byKey.set(key, { key, kind: bot ? "bot" : "user", id: senderId, label });
  }
  const list = Array.from(byKey.values());
  list.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  return list.map((row) => ({
    ...row,
    subtitle: row.kind === "bot" ? "Bot" : isCurrentUserId(row.id) ? "You" : "Member",
    isBot: row.kind === "bot",
    isSelf: row.kind === "user" && isCurrentUserId(row.id),
  }));
}

export function clusterHistoryMessages(items) {
  const clusters = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (isMetadataAutoReplyMessage(item)) {
      clusters.push({ type: "auto", item });
      i++;
      continue;
    }
    if (isCallLogMessage(item)) {
      clusters.push({ type: "call", item });
      i++;
      continue;
    }
    if (isStandaloneSystemLineMessage(item)) {
      clusters.push({ type: "system", item });
      i++;
      continue;
    }
    const key = getSenderGroupKey(item);
    if (!key) {
      clusters.push({ type: "chat", items: [item] });
      i++;
      continue;
    }
    const group = [item];
    i++;
    while (
      i < items.length &&
      !isStandaloneSystemLineMessage(items[i]) &&
      !isCallLogMessage(items[i]) &&
      getSenderGroupKey(items[i]) === key
    ) {
      group.push(items[i]);
      i++;
    }
    clusters.push({ type: "chat", items: group });
  }
  return clusters;
}

/**
 * Batch form of fetchUserDisplayProfileEntry: resolves every id in ONE request and returns the same
 * [id, {label, avatar}] entries, including id-only fallbacks for anything unresolved. A failure
 * degrades the whole set to fallbacks rather than throwing, matching the single-id helper.
 */
export async function fetchUserDisplayProfileEntries(userIds) {
  const ids = Array.isArray(userIds) ? userIds : [];
  const fallback = (id) => [id, { label: id, avatar: "" }];
  if (ids.length === 0) {
    return [];
  }
  try {
    const profiles = await fetchUserChatProfiles({ userIds: ids });
    return ids.map((id) => {
      const profile = profiles[id];
      if (!profile) return fallback(id);
      return [id, { label: profile.displayName || id, avatar: String(profile.avatar || "").trim() }];
    });
  } catch (_error) {
    return ids.map(fallback);
  }
}

export async function fetchUserDisplayProfileEntry(userId) {
  try {
    const profile = await fetchUserChatProfile({ userId });
    return [
      userId,
      {
        label: profile.displayName || userId,
        avatar: String(profile?.avatar || "").trim(),
      },
    ];
  } catch (_error) {
    return [
      userId,
      {
        label: userId,
        avatar: "",
      },
    ];
  }
}

export function isSenderBotMessage(message) {
  return String(message?.sender?.kind || "").toLowerCase() === "bot";
}

export function getThreadInitials(threadName) {
  const name = String(threadName || "").trim();
  if (!name) return "GR";
  const tokens = name.split(/\s+/).filter(Boolean);
  const prefixTokens = new Set(["whatsapp", "wa"]);
  const trimmedTokens = [...tokens];
  while (trimmedTokens.length > 1 && prefixTokens.has(trimmedTokens[0].replace(/[^a-z]/gi, "").toLowerCase())) {
    trimmedTokens.shift();
  }
  if (trimmedTokens.length === 0) return "GR";
  // First name + last name initials (e.g. "Shakira Linya … Alam" → "SA", "Jacek Stepien" → "JS").
  const firstInitial = trimmedTokens[0][0]?.toUpperCase() || "";
  if (trimmedTokens.length === 1) return firstInitial;
  const lastInitial = trimmedTokens[trimmedTokens.length - 1][0]?.toUpperCase() || "";
  return firstInitial + lastInitial;
}

export function getThreadPreview(thread) {
  return thread?.lastMessagePreview || "No messages yet";
}

export function getThreadTime(thread) {
  const rawTime = thread?.lastMessageAt;
  if (!rawTime) return "";

  const date = new Date(rawTime);
  if (Number.isNaN(date.getTime())) return "";

  const isToday = appIsSameDay(date, new Date());
  return isToday
    ? date.toLocaleTimeString([], withAppTimeZone({ hour: "2-digit", minute: "2-digit" }))
    : date.toLocaleDateString([], withAppTimeZone({ month: "short", day: "numeric" }));
}

export function getMessageTime(message) {
  const rawTime = message?.createdAt;
  if (!rawTime) return "";
  const date = new Date(rawTime);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], withAppTimeZone({
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }));
}

/**
 * Timestamp for a delivery/seen receipt row. Same shape as getMessageTime and, crucially, the same
 * timezone: a receipt row reading a different clock than the bubble beside it looks like a bug.
 * Takes a raw ISO string rather than a message, since a receipt is not a message.
 */
export function formatReceiptTime(raw) {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], withAppTimeZone({
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }));
}

/**
 * Compact call duration for call log rows: "13s", "2m 13s", "1h 2m".
 */
export function formatCallDuration(durationSeconds) {
  const total = Number.isFinite(durationSeconds) ? Math.max(0, Math.floor(durationSeconds)) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function getMessageText(message) {
  // WhatsApp group system lines carry a structured body ({event, actorName, …}); the generic
  // parser would render just the capitalized event ("Participant add"). Format the human line
  // instead — its own body.text fallback covers events this build doesn't know yet.
  if (String(message?.contentType || "").trim().toLowerCase() === "group.system") {
    const line = formatWaGroupSystemLine(message);
    if (line) {
      return line;
    }
  }
  const text = parseMessageBodyText(message?.body);
  const trimmed = String(text || "").trim();
  if (trimmed) {
    return trimmed;
  }
  const raw = message?.attachments;
  const hasAttachments =
    raw != null &&
    (Array.isArray(raw) ? raw.length > 0 : typeof raw === "object" || typeof raw === "string");
  if (hasAttachments) {
    return "";
  }
  return "No content";
}

export function formatAttachmentSize(size) {
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Compact label for a pending/snooze deadline: time of day when it lands today
 * ("2:00 PM"), "Tomorrow 9:00 AM" for the next day, weekday beyond that.
 * Null for missing/invalid input (render nothing).
 */
export function formatPendingUntilShort(iso, nowMs = Date.now()) {
  if (!iso) return null;
  const until = new Date(iso);
  if (Number.isNaN(until.getTime())) return null;
  const now = new Date(nowMs);
  const time = until.toLocaleTimeString(undefined, withAppTimeZone({ hour: "numeric", minute: "2-digit" }));
  const dayDiff = appDayDiff(until, now);
  if (dayDiff === 0) {
    return time;
  }
  if (dayDiff === 1) {
    return `Tomorrow ${time}`;
  }
  return until.toLocaleDateString(undefined, withAppTimeZone({ weekday: "short", month: "short", day: "numeric" }));
}

/**
 * Groups a lastMessage-desc sorted thread list into day buckets for the chronological lens's
 * sticky separators. Returns [{ key, label ("TODAY"/"YESTERDAY"/weekday), dateLabel ("12 Jun"),
 * threads }]; threads without a timestamp land in a trailing "Earlier" bucket.
 */
export function groupThreadsByDay(threads, nowMs = Date.now()) {
  const now = new Date(nowMs);
  const groups = [];
  const groupsByKey = new Map();

  for (const thread of threads || []) {
    const t = new Date(thread?.lastMessageAt || "").getTime();
    let key = "earlier";
    let label = "Earlier";
    let dateLabel = "";
    if (!Number.isNaN(t) && thread?.lastMessageAt) {
      const date = new Date(t);
      // Bucket + label by the civil day IN THE APP ZONE, so separators line up with the times
      // shown on each row (both zone-aware).
      key = `day:${appDayKey(date)}`;
      dateLabel = date.toLocaleDateString(undefined, withAppTimeZone({ day: "numeric", month: "short" }));
      const dayDiff = appDayDiff(now, date);
      if (dayDiff <= 0) label = "Today";
      else if (dayDiff === 1) label = "Yesterday";
      else label = date.toLocaleDateString(undefined, withAppTimeZone({ weekday: "long" }));
    }
    let group = groupsByKey.get(key);
    if (!group) {
      group = { key, label, dateLabel, threads: [] };
      groupsByKey.set(key, group);
      groups.push(group);
    }
    group.threads.push(thread);
  }
  return groups;
}
